import { useMemo } from "react";
import { useData } from "@hooks/useData";
import http from "@core/fetch";
import apiConfig from "@apiConfig";
import { components } from "@apiSchema";
import { ID } from "@models";

type ApiCategoryProfileDetails = components["schemas"]["SnapCategoryProfileDetailsModel"];
export type CategoryOverview = Omit<components["schemas"]["SnapRelatedCategoryReport"], "id"> & { id: number };
export type CategoryProfileRelatedProfile = Omit<
    components["schemas"]["SnapCategoryProfileKeyword"],
    "wordId" | "wordName"
> & {
    wordId: number;
    wordName: string;
};
export type CategoryProfileRelatedChart = Omit<
    components["schemas"]["SnapCategoryProfileRelatedChart"],
    "id" | "name"
> & {
    id: number;
    name: string;
};
export type ICategoryProfileDetails = Omit<
    ApiCategoryProfileDetails,
    "categoryOverviews" | "relatedProfiles" | "relatedCharts"
> & {
    categoryOverviews: CategoryOverview[];
    relatedProfiles: CategoryProfileRelatedProfile[];
    relatedCharts: CategoryProfileRelatedChart[];
};

const REQUEST_TYPE: keyof typeof apiConfig = 'get category profile details';

async function getCategoryProfileDetails(id: ID): Promise<ApiCategoryProfileDetails | null> {
    try {
        return await http.get(REQUEST_TYPE, { id }) as ApiCategoryProfileDetails;
    } catch (error) {
        console.error('Fetch error:', REQUEST_TYPE, error);
        return null;
    }
}

export default function useCategoryProfileDetails(id: ID) {
    const { data, isLoading } = useData<ApiCategoryProfileDetails | null>(null, getCategoryProfileDetails, id);

    const details = useMemo<ICategoryProfileDetails | null>(
        () => data && {
            ...data,
            categoryOverviews: (data.categoryOverviews ?? []).filter(
                (overview): overview is CategoryOverview => typeof overview.id === "number",
            ),
            relatedProfiles: (data.relatedProfiles ?? []).filter(
                (profile): profile is CategoryProfileRelatedProfile =>
                    typeof profile.wordId === "number" && typeof profile.wordName === "string",
            ),
            relatedCharts: (data.relatedCharts ?? []).filter(
                (chart): chart is CategoryProfileRelatedChart =>
                    typeof chart.id === "number" && typeof chart.name === "string",
            ),
        },
        [data],
    );

    return { data: details, isLoading };
}
